import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Filter, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { FilterOption } from "@/components/ui/filter-select";

const MAX_LABELS_IN_SUMMARY = 2;
const EMPTY_SUMMARY = "Todos";

interface FilterMultiSelectProps<T extends string> {
  options: FilterOption<T>[];
  values: T[];
  onToggle: (value: T) => void;
  onClear: () => void;
  label?: string;
  placeholder?: string;
  className?: string;
  popoverWidth?: string;
  modal?: boolean;
}

function buildSummary<T extends string>(
  options: FilterOption<T>[],
  values: T[],
): string {
  if (values.length === 0) return EMPTY_SUMMARY;
  if (values.length > MAX_LABELS_IN_SUMMARY) return `${values.length} selecionados`;
  return options
    .filter((option: FilterOption<T>) => values.includes(option.value))
    .map((option: FilterOption<T>) => option.label)
    .join(", ");
}

export function FilterMultiSelect<T extends string>({
  options,
  values,
  onToggle,
  onClear,
  label = "Filtro",
  placeholder,
  className,
  popoverWidth = "w-[200px]",
  modal = true,
}: FilterMultiSelectProps<T>): JSX.Element {
  const summary: string = buildSummary(options, values);

  return (
    <Popover modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-8 border-dashed", className)}
        >
          <Filter className="mr-2 h-4 w-4" />
          {placeholder || label}
          <div className="mx-2 h-4 w-[1px] bg-gray-300" />
          <Badge variant="secondary" className="rounded-sm px-1 font-normal">
            {summary}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-0", popoverWidth)} align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              <CommandItem onSelect={onClear} className="cursor-pointer">
                <div className="flex items-center gap-2 w-full">
                  <div
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-sm border",
                      values.length === 0
                        ? "bg-primary border-primary"
                        : "border-gray-300"
                    )}
                  >
                    {values.length === 0 && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <span>{EMPTY_SUMMARY}</span>
                </div>
              </CommandItem>
              {options.map((option: FilterOption<T>) => {
                const isSelected: boolean = values.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => onToggle(option.value)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <div
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded-sm border",
                          isSelected
                            ? "bg-primary border-primary"
                            : "border-gray-300"
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <span>{option.label}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
